import { useEffect, useRef, useCallback, useState } from "react";

const WARNING_THRESHOLD_SEC = 300; // 5 minutes
const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
const DEFAULT_DEBOUNCE_MS = 1000;

/**
 * Decode JWT payload without verification (client-side only to read exp).
 * Returns exp in seconds, or null if invalid/missing.
 */
function getExpFromToken(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Get token expiry timestamp (ms). Prefers sessionStorage authTokenExpiry,
 * falls back to decoding JWT exp.
 */
function getTokenExpiryAt(): number | null {
  const stored = sessionStorage.getItem("authTokenExpiry");
  if (stored) {
    const n = parseInt(stored, 10);
    if (!isNaN(n)) return n;
  }
  const token = sessionStorage.getItem("authToken");
  if (!token) return null;
  const exp = getExpFromToken(token);
  if (exp == null) return null;
  return exp * 1000;
}

export type ExpiryReason = "token" | "idle";

export interface UseSessionExpiryResult {
  secondsRemaining: number;
  isWarningActive: boolean;
  isExpired: boolean;
  expiryReason: ExpiryReason | null;
  resetIdle: () => void;
}

/**
 * Unified session expiry: token (45 min) and idle (20 min).
 * Warning banner shows when either has <= 5 min left; countdown is min of both.
 * Replaces separate useIdleTimeout for logout; call onLogout when countdown hits 0.
 */
export function useSessionExpiry(
  onLogout: () => void,
  enabled: boolean = true
): UseSessionExpiryResult {
  const [secondsRemaining, setSecondsRemaining] = useState(Number.MAX_SAFE_INTEGER);
  const [expiryReason, setExpiryReason] = useState<ExpiryReason | null>(null);
  const lastActivityRef = useRef(Date.now());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  const resetIdle = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const computeRemaining = useCallback((): { sec: number; reason: ExpiryReason | null } => {
    const now = Date.now();
    const tokenExpiryAt = getTokenExpiryAt();
    const tokenSec = tokenExpiryAt != null
      ? Math.max(0, Math.floor((tokenExpiryAt - now) / 1000))
      : Number.MAX_SAFE_INTEGER;
    const idleDeadlineAt = lastActivityRef.current + IDLE_TIMEOUT_MS;
    const idleSec = Math.max(0, Math.floor((idleDeadlineAt - now) / 1000));
    if (tokenSec <= 0 || idleSec <= 0) {
      const reason = tokenSec <= idleSec ? "token" : "idle";
      return { sec: 0, reason };
    }
    if (idleSec <= tokenSec) return { sec: idleSec, reason: "idle" };
    return { sec: tokenSec, reason: "token" };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        lastActivityRef.current = Date.now();
        debounceRef.current = null;
      }, DEFAULT_DEBOUNCE_MS);
    };

    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"];
    events.forEach((e) => document.addEventListener(e, handleActivity));

    const intervalId = setInterval(() => {
      const { sec, reason } = computeRemaining();
      setSecondsRemaining(sec);
      setExpiryReason(reason);
      if (sec <= 0) {
        onLogoutRef.current();
      }
    }, 1000);

    // Initial value
    const { sec, reason } = computeRemaining();
    setSecondsRemaining(sec);
    setExpiryReason(reason);
    if (sec <= 0) onLogoutRef.current();

    return () => {
      events.forEach((e) => document.removeEventListener(e, handleActivity));
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      clearInterval(intervalId);
    };
  }, [enabled, computeRemaining]);

  const isWarningActive = secondsRemaining > 0 && secondsRemaining <= WARNING_THRESHOLD_SEC;
  const isExpired = secondsRemaining <= 0;

  return {
    secondsRemaining,
    isWarningActive,
    isExpired,
    expiryReason,
    resetIdle,
  };
}
